import { useEffect, useRef, useState } from 'react';
import { emailValidator } from 'darwin_core/validators.js';

import { useAuth } from './AuthenticationContext.jsx';
import BackLink from '../navigation/BackLink.jsx';
import { passkeyCancelled, passkeysSupported } from '../passkeys/webauthn.js';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import PasswordField from '../components/PasswordField.jsx';
import TextField from '../components/TextField.jsx';

// Error copy mirrors the mobile app's SignIn (app/src/screens/Auth/SignIn.js),
// which is the reference for every string on this screen. The app shows these in
// an alert dialog; here they render inline under the form, which is the web
// convention and what the fields' aria-describedby points at.

// Bad credentials. The app deliberately gives the same message whether the
// account exists or not, so a sign-in attempt can't be used to enumerate emails.
const INCORRECT_CREDENTIALS = 'Incorrect username or password.';

// Everything that isn't one of the recognised cases maps to this single generic
// message, so a raw Cognito exception (which can carry internal detail) is never
// rendered to the user.
const GENERIC_ERROR = 'Please check your account details, or try again later.';

// Cognito raises its exceptions by `name`; the two "next step" cases are re-thrown
// by our auth context as `new Error(code)` (code in `message`), so match on both.
function messageForError(error) {
  const codes = [error && error.name, error && error.message];
  if (
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException')
  ) {
    return INCORRECT_CREDENTIALS;
  }
  if (codes.includes('PasswordResetRequiredException')) {
    // The app sends the user to its in-app code entry here. That needs a code,
    // which only the reset email brings, so point at the reset flow that sends
    // one.
    return 'Your password needs to be reset. Use “Forgot Password?” to set a new one.';
  }
  if (codes.includes('UserNotConfirmedException')) {
    // The app navigates to its email-confirmation screen; web can't, so say where
    // to finish.
    return 'Your account isn’t confirmed yet. Open the Halo mobile app to finish confirming it, then log in here.';
  }
  return GENERIC_ERROR;
}

// The user closed the browser's prompt, or the browser had no passkey for Halo:
// browsers report both the same way on purpose.
const PASSKEY_NOT_USED =
  'No passkey was used. Try again, or log in with your password.';

// One message whether the account doesn't exist, has no such passkey, can't
// sign in, needs its password reset or isn't confirmed, so passkey sign-in
// can't be used to probe accounts. Unlike a password sign-in, it starts with
// nothing but the email, so naming those last two states would tell anyone
// who typed an address that the account exists. The reset hint applies to
// everyone, so it gives nothing away: a reset removes the account's passkeys,
// but the password manager still offers them.
const PASSKEY_NOT_ACCEPTED =
  'We couldn’t sign you in with a passkey. Check your email, or log in with your password. If you’ve reset your password, your passkeys were removed: log in with your password and add them again.';

function messageForPasskeyError(error) {
  if (passkeyCancelled(error)) {
    return PASSKEY_NOT_USED;
  }
  const codes = [error && error.name, error && error.message];
  if (
    codes.includes('PasskeyNotAccepted') ||
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException') ||
    codes.includes('PasswordResetRequiredException') ||
    codes.includes('UserNotConfirmedException')
  ) {
    return PASSKEY_NOT_ACCEPTED;
  }
  return GENERIC_ERROR;
}

export default function SignIn({ onForgotPassword, autoFocusHeading = false }) {
  const { signIn, signInWithPasskey } = useAuth();
  const [passkeysWork] = useState(passkeysSupported);
  // 'email' asks for the email alone; 'password' then asks for the password,
  // or offers this browser's passkey for that email, as the app's two screens
  // do.
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const headingRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  // Set when the step changes, so focus moves to the field the step asks for
  // once it's on screen, rather than staying on a button that has gone.
  const [focusField, setFocusField] = useState(null);

  useEffect(() => {
    if (focusField) {
      (focusField === 'password' ? passwordRef : emailRef).current?.focus();
      setFocusField(null);
    }
  }, [focusField]);

  const address = email.trim();

  // Only steal focus when this view is re-shown by a toggle (back from
  // forgot-password), not on the initial gate render.
  useEffect(() => {
    if (autoFocusHeading) {
      headingRef.current?.focus();
    }
  }, [autoFocusHeading]);

  // One sign-in at a time, password or passkey. `pending` disables the
  // buttons, but only once React re-renders: a fast second click, or Enter in
  // the password field, could start another first. A second passkey sign-in
  // replaces the first's Cognito challenge, so the passkey answering the first
  // would be refused.
  const signingIn = useRef(false);

  const handleNext = async () => {
    setError(null);
    const validation = await emailValidator(address);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setEmail(address);
    setStep('password');
    setFocusField('password');
  };

  const handleChangeEmail = () => {
    if (signingIn.current) {
      return;
    }
    setError(null);
    // So another email doesn't arrive at its password step with this one's
    // password filled in, where Enter would send it.
    setPassword('');
    setStep('email');
    setFocusField('email');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (step === 'email') {
      await handleNext();
      return;
    }
    if (signingIn.current) {
      return;
    }
    signingIn.current = true;
    setError(null);
    setPending(true);
    try {
      const session = await signIn(email, password);
      if (session) {
        // A real success flips `authenticated`, which re-gates the app and
        // unmounts this form, so no state update is needed on that path.
        return;
      }
      // signIn resolved but no session materialised (no tokens / a race). The
      // gate won't unmount us, so surface feedback rather than leaving the form
      // stuck disabled with no explanation.
      setError(GENERIC_ERROR);
      setPending(false);
    } catch (ex) {
      setError(messageForError(ex));
      setPending(false);
    } finally {
      signingIn.current = false;
    }
  };

  const handlePasskey = async () => {
    if (signingIn.current) {
      return;
    }
    setError(null);
    signingIn.current = true;
    setPending(true);
    try {
      const session = await signInWithPasskey(address);
      if (session) {
        // As with a password sign-in, success re-gates the app and unmounts us.
        return;
      }
      setError(GENERIC_ERROR);
      setPending(false);
    } catch (ex) {
      setError(messageForPasskeyError(ex));
      setPending(false);
    } finally {
      signingIn.current = false;
    }
  };

  // Layout and copy follow the app's SignIn: the devices illustration, the green
  // "Welcome to Halo," title, placeholder-labelled Email and Password fields with
  // "Forgot Password?" tucked under the password, then the button (Continue on
  // the email step, Sign in on the password step). The app
  // also offers "Create an account" and "Try demo"; both are out of scope for web.
  // Below the form sits the legal footer: the app carries those two links on its
  // sign-up screen, which web does not have (see legal/LegalFooter.jsx).
  // The fields are TextFields: placeholder-labelled like the app, with a hidden
  // <label> for assistive tech (see components/TextField.jsx for the rationale).
  // The email is the login's username, as on the reset screen, so a password
  // manager files both under the same account. The password step keeps it on
  // screen, read-only, for the same reason; the back link changes it.
  return (
    <main className="app-shell auth-screen">
      {step === 'password' ? (
        <div className="auth-screen__nav">
          <BackLink onClick={handleChangeEmail} />
        </div>
      ) : null}
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__title"
        ref={headingRef}
        tabIndex={-1}
      >
        Welcome to Halo,
      </h1>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <TextField
          id="signin-email"
          name="email"
          type="email"
          label="Email"
          autoComplete="username"
          ref={emailRef}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          readOnly={step === 'password'}
          disabled={pending}
          required
          errorId={error ? 'signin-error' : undefined}
        />

        {step === 'password' ? (
          <>
            <PasswordField
              id="signin-password"
              name="password"
              label="Password"
              className="text-field--tight"
              autoComplete="current-password"
              ref={passwordRef}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={pending}
              required
              errorId={error ? 'signin-error' : undefined}
            />

            {/* type="button" so Enter in a field still submits the form (the
                submit button below is the form's default), and the link can
                never submit. */}
            <button
              type="button"
              className="link-button auth-form__link"
              onClick={onForgotPassword}
            >
              Forgot Password?
            </button>
          </>
        ) : null}

        {/* Always present so assistive tech has the live region in the tree
            before an error is set; role=alert announces it when populated. The id
            lets the fields point their aria-describedby at it while it holds an
            error (the fields' errorId above). Collapses to nothing while empty. */}
        <p
          id="signin-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>

        <button type="submit" className="auth-form__submit" disabled={pending}>
          {/* The email step only moves on to the password, so it says so. */}
          {pending ? 'Signing in…' : step === 'email' ? 'Continue' : 'Sign in'}
        </button>

        {/* For every email, wherever the browser can use passkeys: the browser
            says itself when it has none for the site, and can offer a phone
            nearby. Nothing tells the page whether an email has a passkey, so
            the login gives nothing away. type="button" so Enter in a field
            still submits the password form. */}
        {step === 'password' && passkeysWork ? (
          <button
            type="button"
            className="btn-secondary auth-form__passkey"
            onClick={handlePasskey}
            disabled={pending}
          >
            Sign in with a passkey
          </button>
        ) : null}
      </form>
      <LegalFooter />
    </main>
  );
}
